/**
 * useEmailThreads Hook
 *
 * Custom hooks for fetching email threads and thread details.
 */

import { useQuery } from '@tanstack/react-query';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface EmailThread {
  id: string;
  subject: string;
  gmail_thread_id: string | null;
  customer_id: string | null;
  contact_id: string | null;
  project_id: string | null;
  last_message_at: string | null;
  message_count: number;
  is_archived: boolean;
  created_at: string;
}

interface EmailThreadListResponse {
  items: EmailThread[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

interface Attachment {
  filename: string;
  mime_type: string;
  size: number;
  gmail_attachment_id: string;
}

interface EmailMessage {
  id: string;
  thread_id: string;
  gmail_message_id: string | null;
  direction: 'inbound' | 'outbound';
  from_email: string;
  from_name: string | null;
  to_emails: string[];
  cc_emails: string[];
  subject: string;
  body_text: string | null;
  body_html: string | null;
  status: string;
  sent_at: string | null;
  received_at: string | null;
  has_attachments: boolean;
  attachments: Attachment[];
  created_at: string;
}

interface EmailThreadDetailResponse {
  id: string;
  subject: string;
  gmail_thread_id: string | null;
  customer_id: string | null;
  contact_id: string | null;
  customer_name: string | null;
  contact_name: string | null;
  last_message_at: string | null;
  message_count: number;
  is_archived: boolean;
  created_at: string;
  messages: EmailMessage[];
}

interface UseEmailThreadsOptions {
  customerId?: string;
  contactId?: string;
  projectId?: string;
  includeArchived?: boolean;
  page?: number;
  perPage?: number;
  enabled?: boolean;
}

async function fetchEmailThreads(
  options: UseEmailThreadsOptions
): Promise<EmailThreadListResponse> {
  const params = new URLSearchParams();

  if (options.customerId) params.set('customer_id', options.customerId);
  if (options.contactId) params.set('contact_id', options.contactId);
  if (options.projectId) params.set('project_id', options.projectId);
  if (options.includeArchived) params.set('include_archived', 'true');
  if (options.page) params.set('page', options.page.toString());
  if (options.perPage) params.set('per_page', options.perPage.toString());

  const response = await fetch(`${API_BASE_URL}/emails?${params}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Fehler beim Laden der E-Mails: ${response.statusText}`);
  }

  return response.json();
}

export function useEmailThreads(options: UseEmailThreadsOptions = {}) {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: ['email-threads', queryOptions],
    queryFn: () => fetchEmailThreads(queryOptions),
    enabled,
    staleTime: 60 * 1000, // 1 minute
  });
}

async function fetchEmailThread(threadId: string): Promise<EmailThreadDetailResponse> {
  const response = await fetch(`${API_BASE_URL}/emails/thread/${threadId}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('E-Mail-Thread nicht gefunden');
    }
    throw new Error(`Fehler beim Laden: ${response.statusText}`);
  }

  return response.json();
}

export function useEmailThread(threadId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['email-thread', threadId],
    queryFn: () => fetchEmailThread(threadId!),
    enabled: enabled && !!threadId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Hook for email history (all messages for a customer/contact)
interface EmailHistoryEntry {
  id: string;
  thread_id: string;
  direction: 'inbound' | 'outbound';
  subject: string;
  from_email: string;
  from_name: string | null;
  body_preview: string | null;
  sent_at: string | null;
  received_at: string | null;
  has_attachments: boolean;
}

interface EmailHistoryResponse {
  entries: EmailHistoryEntry[];
  total: number;
  has_more: boolean;
}

async function fetchEmailHistory(
  type: 'customer' | 'contact',
  id: string,
  limit: number,
  offset: number
): Promise<EmailHistoryResponse> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });

  const response = await fetch(
    `${API_BASE_URL}/emails/history/${type}/${id}?${params}`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Fehler beim Laden: ${response.statusText}`);
  }

  return response.json();
}

export function useCustomerEmailHistory(
  customerId: string,
  options: { limit?: number; offset?: number; enabled?: boolean } = {}
) {
  const { limit = 20, offset = 0, enabled = true } = options;

  return useQuery({
    queryKey: ['email-history', 'customer', customerId, { limit, offset }],
    queryFn: () => fetchEmailHistory('customer', customerId, limit, offset),
    enabled: enabled && !!customerId,
    staleTime: 60 * 1000,
  });
}

export function useContactEmailHistory(
  contactId: string,
  options: { limit?: number; offset?: number; enabled?: boolean } = {}
) {
  const { limit = 20, offset = 0, enabled = true } = options;

  return useQuery({
    queryKey: ['email-history', 'contact', contactId, { limit, offset }],
    queryFn: () => fetchEmailHistory('contact', contactId, limit, offset),
    enabled: enabled && !!contactId,
    staleTime: 60 * 1000,
  });
}
